import { memo, useCallback } from 'react';
import Link from 'next/link';
import styled, { type CSSObject } from '@emotion/styled';
import { cx, css } from '@emotion/css';

import type { UrlObject } from 'url';
import type { ThemeProps } from '@emotion/react';

import { colorAlpha } from '../../../lib/colorAlpha';
import { themeFont } from '../../../lib/themeFont';
import { useCurrentRoute } from '../../../lib/useCurrentRoute';
import { handleSinglePageSectionScrolling } from '../../Templates/SinglePageTemplate';

interface LinkTitleProps {
  active: boolean;
  desktop: boolean;
}

const anchorClass = css`
  display: block;
  padding: 0;
  margin: 0;

  :hover {
    text-decoration: none !important;
  }
`;

const LinkTitle = styled('span')<LinkTitleProps>`
  ${themeFont('s6')}
  border-bottom: 2px solid transparent;
  display: block;
  letter-spacing: 2px;
  line-height: 1;
  margin: 0 auto;
  padding: 8px 8px 6px;
  text-decoration: none;
  transition: all 0.25s;
  width: fit-content;

  ${({ active, desktop, theme }: LinkTitleProps & ThemeProps): string =>
    desktop && !active
      ? `
    :hover {
      color: ${theme.colors.primaryHover};
      border-bottom: 2px solid ${colorAlpha(theme.colors.primaryHover, '70%')};
    }
  `
      : ''}

  ${({ active, theme }: LinkTitleProps & ThemeProps): string =>
    active
      ? `
    border-bottom: 2px solid ${theme.fonts.s5.color};
  `
      : ''}

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.components.Nav?.Link}

  ${({ active, theme }: LinkTitleProps & ThemeProps): CSSObject | undefined =>
    active ? theme.components.Nav?.LinkActive : undefined}
`;

interface NavLinkProps {
  desktop: boolean;
  handleTracking(selection: string): void;
  title: string;
  route: string;
  href: string | UrlObject;
  as: string;
  className?: string | undefined;
  setMobileNavOpen?: ((open: boolean) => void) | undefined;
  shouldScrollToTopOnNavigate?: boolean | undefined;
  activeSinglePageSectionId?: string | undefined;
}

const noop = (): void => {
  return;
};

function NavLink({
  setMobileNavOpen = noop,
  className,
  desktop,
  handleTracking,
  href,
  title,
  route,
  as,
  shouldScrollToTopOnNavigate = true,
  activeSinglePageSectionId,
}: NavLinkProps) {
  const currentRoute = useCurrentRoute();

  const active =
    activeSinglePageSectionId !== undefined
      ? activeSinglePageSectionId === route
      : currentRoute === route;

  const noFollow = !['', 'registry'].includes(route);

  const onClick = useCallback((): void => {
    setMobileNavOpen(false);
    handleTracking(route);

    // Only Single Page should scroll to section
    if (!shouldScrollToTopOnNavigate) {
      handleSinglePageSectionScrolling(route);
    }
  }, [handleTracking, route, setMobileNavOpen, shouldScrollToTopOnNavigate]);

  return (
    <Link
      className={cx(anchorClass, className)}
      href={href}
      as={as}
      rel={noFollow ? 'nofollow' : undefined}
      onClick={onClick}
      scroll={shouldScrollToTopOnNavigate}
    >
      <LinkTitle active={active} desktop={desktop}>
        {title}
      </LinkTitle>
    </Link>
  );
}

export default memo(NavLink);
